import { requiresValue } from "@/src/constants/analyticsFilterConstants.js";
import { isPresent, hasValidArrayValue } from "@/src/utils/utils.js";
import { analyticsHardcodedSchemaObjects } from "@/src/constants/objectConstants.js";
import {
  analyticsCharts,
  apexChartTypes,
  apexChartTypeMap,
  nonApexChartTypes,
  measureDataTypes,
  dateDataTypes,
  booleanDataTypes,
  stringDataTypes,
  configDataTypeCategories,
  chartFieldConfigDetails,
  fieldConfigVisibility,
  chartValidationRules,
  chartConfigurations,
  filterFieldConfig,
  filterValidationMessages,
  analyticsDashboardStatuses,
} from "@/src/constants/analyticConstants.js";
import { aggregationOperators } from "@/src/constants/formulaConstants.js";

export const getApexChartType = (analyticsChartType) => {
  try {
    return apexChartTypeMap[analyticsChartType] || apexChartTypes.BAR;
  } catch (error) {
    console.error("Error getting apex chart type: ", error);
    return apexChartTypes.BAR;
  }
};

export const isHorizontalChart = (chartType) => {
  try {
    return chartType === analyticsCharts.HORIZONTAL;
  } catch (error) {
    console.error("Error checking horizontal chart: ", error);
    return false;
  }
};

export const isNonApexChart = (chartType) => {
  try {
    return nonApexChartTypes.includes(chartType);
  } catch (error) {
    console.error("Error checking non-apex chart: ", error);
    return false;
  }
};

export const isKPIChart = (chartType) => {
  try {
    return chartType === analyticsCharts.KPI;
  } catch (error) {
    console.error("Error checking KPI chart: ", error);
    return false;
  }
};

export const isTableChart = (chartType) => {
  try {
    return chartType === analyticsCharts.TABLE;
  } catch (error) {
    console.error("Error checking table chart: ", error);
    return false;
  }
};

export const isFunnelChart = (chartType) => {
  try {
    return chartType === analyticsCharts.FUNNEL;
  } catch (error) {
    console.error("Error checking funnel chart: ", error);
    return false;
  }
};

export const isPivotTableChart = (chartType) => {
  try {
    return chartType === analyticsCharts.PIVOT_TABLE;
  } catch (error) {
    console.error("Error checking pivot table chart: ", error);
    return false;
  }
};

export const isChartDataEmpty = (chartData) => {
  if (!chartData) return true;
  return !(
    chartData.categories?.length > 0 ||
    chartData.series?.length > 0 ||
    chartData.labels?.length > 0 ||
    chartData.kpis?.length > 0 ||
    chartData.rows?.length > 0 ||
    chartData.stages?.length > 0
  );
};

export const calculateConversionRate = (currentCount, previousCount) => {
  try {
    if (!previousCount || previousCount === 0) return 0;
    return ((currentCount / previousCount) * 100).toFixed(1);
  } catch (error) {
    console.error("Error calculating conversion rate: ", error);
    return 0;
  }
};

export const calculateDropOffRate = (currentCount, previousCount) => {
  try {
    const conversionRate = calculateConversionRate(currentCount, previousCount);
    return (100 - parseFloat(conversionRate)).toFixed(1);
  } catch (error) {
    console.error("Error calculating drop-off rate: ", error);
    return 0;
  }
};

export const isDateString = (str) => {
  try {
    if (!str || typeof str !== "string") return false;

    if (/^(Q[1-4]\s+\d{4}|Week\s+\d+,\s+\d{4}|\d{4})$/.test(str.trim())) {
      return false;
    }

    const cleaned = str
      .replace(/\s*\+\d{4}\s*Utc$/i, "")
      .replace(/\s*UTC$/i, "")
      .trim();
    const date = new Date(cleaned);
    return !isNaN(date.getTime());
  } catch (error) {
    console.error(
      "An error occurred while checking if it is a date string or not: ",
      error,
    );
  }
};

export const formatDateCategory = (dateStr) => {
  try {
    if (!dateStr || typeof dateStr !== "string") return dateStr;

    const cleaned = dateStr
      .replace(/\s*\+\d{4}\s*Utc$/i, "")
      .replace(/\s*UTC$/i, "")
      .trim();

    const date = new Date(cleaned);
    if (isNaN(date.getTime())) return dateStr;

    return date.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch (error) {
    return dateStr;
  }
};

export const formatCategories = (categories) => {
  try {
    if (!categories || !Array.isArray(categories) || categories.length === 0) {
      return [];
    }

    const hasDateCategories = categories.some(isDateString);
    if (!hasDateCategories) return categories;

    return categories.map(formatDateCategory);
  } catch (error) {
    console.error("An error occurred while formatting categories: ", error);
  }
};

export const getConfigDataType = (dataType) => {
  try {
    if (!dataType) return configDataTypeCategories.OTHER;
    const normalizedType = dataType.toLowerCase();

    if (stringDataTypes.some((type) => normalizedType === type)) {
      return configDataTypeCategories.STRING;
    }
    if (measureDataTypes.some((type) => normalizedType.includes(type))) {
      return configDataTypeCategories.NUMBER;
    }
    if (dateDataTypes.some((type) => normalizedType.includes(type))) {
      return configDataTypeCategories.DATE;
    }
    if (booleanDataTypes.some((type) => normalizedType.includes(type))) {
      return configDataTypeCategories.BOOLEAN;
    }
    if (stringDataTypes.some((type) => normalizedType.includes(type))) {
      return configDataTypeCategories.STRING;
    }
    return configDataTypeCategories.OTHER;
  } catch (error) {
    console.error("Error getting config data type: ", error);
    return configDataTypeCategories.OTHER;
  }
};

export const shouldShowFieldConfig = (field, dataType) => {
  try {
    if (!field) return false;

    const fieldConfig = chartFieldConfigDetails[field];
    if (!fieldConfig || !fieldConfig.component) return false;

    if (!fieldConfigVisibility[field]) return false;

    const configType = getConfigDataType(dataType);
    if (configType === configDataTypeCategories.OTHER) return false;

    return true;
  } catch (error) {
    console.error("Error checking field config visibility: ", error);
    return false;
  }
};

export const getFieldComponent = (field) => {
  try {
    if (!field) return null;
    const fieldConfig = chartFieldConfigDetails[field];
    if (!fieldConfig || !fieldConfigVisibility[field]) return null;
    return fieldConfig.component || null;
  } catch (error) {
    console.error("Error getting field component: ", error);
    return null;
  }
};

const isIndianLocale = () => {
  try {
    const locale = navigator?.language || "en-US";
    if (locale === "en-IN" || locale.startsWith("hi")) return true;

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (timezone.includes("Kolkata") || timezone.includes("Asia/Calcutta"))
      return true;

    return false;
  } catch (error) {
    return false;
  }
};

export const formatIndianNumber = (value, decimalPlaces) => {
  try {
    if (value === null || value === undefined) return "";
    const num = Number(value);
    if (isNaN(num)) return value;

    const absNum = Math.abs(num);
    const sign = num < 0 ? "-" : "";
    const maxFraction = decimalPlaces !== undefined && decimalPlaces !== null ? decimalPlaces : 2;

    if (absNum >= 1e7) {
      const crValue = absNum / 1e7;
      return `${sign}${crValue.toLocaleString("en-IN", { maximumFractionDigits: maxFraction })} Cr`;
    }
    if (absNum >= 1e5) {
      const lakhValue = absNum / 1e5;
      return `${sign}${lakhValue.toLocaleString("en-IN", { maximumFractionDigits: maxFraction })} L`;
    }
    if (absNum >= 1e3) {
      const kValue = absNum / 1e3;
      return `${sign}${kValue.toLocaleString("en-IN", { maximumFractionDigits: maxFraction })} K`;
    }
    return num.toLocaleString("en-IN", { maximumFractionDigits: maxFraction });
  } catch (error) {
    return value;
  }
};

export const formatCompactNumber = (value, decimalPlaces) => {
  try {
    if (value === null || value === undefined) return "";
    const num = Number(value);
    if (isNaN(num)) return value;

    if (isIndianLocale()) {
      return formatIndianNumber(num, decimalPlaces);
    }

    const locale = navigator?.language || "en-US";
    const absNum = Math.abs(num);
    const sign = num < 0 ? "-" : "";
    const maxFraction = decimalPlaces !== undefined && decimalPlaces !== null ? decimalPlaces : 1;

    if (absNum >= 1e12) {
      return `${sign}${(absNum / 1e12).toLocaleString(locale, { maximumFractionDigits: maxFraction })}T`;
    }
    if (absNum >= 1e9) {
      return `${sign}${(absNum / 1e9).toLocaleString(locale, { maximumFractionDigits: maxFraction })}B`;
    }
    if (absNum >= 1e6) {
      return `${sign}${(absNum / 1e6).toLocaleString(locale, { maximumFractionDigits: maxFraction })}M`;
    }
    if (absNum >= 1e3) {
      return `${sign}${(absNum / 1e3).toLocaleString(locale, { maximumFractionDigits: maxFraction })}K`;
    }
    const smallFraction = decimalPlaces !== undefined && decimalPlaces !== null ? decimalPlaces : 0;
    return num.toLocaleString(locale, { maximumFractionDigits: smallFraction });
  } catch (error) {
    return value;
  }
};

export const formatLocaleNumber = (value, options = {}) => {
  try {
    if (value === null || value === undefined) return "";
    const num = Number(value);
    if (isNaN(num)) return value;

    if (isIndianLocale()) {
      return formatIndianNumber(num, options.decimalPlaces);
    }

    const locale = navigator?.language || "en-US";
    return num.toLocaleString(locale, {
      maximumFractionDigits: options.decimalPlaces ?? 2,
      minimumFractionDigits: options.minDecimalPlaces ?? 0,
      ...options,
    });
  } catch (error) {
    return value;
  }
};

export const formatFullNumber = (value, prefix = "", suffix = "") => {
  try {
    if (value === null || value === undefined) return "";
    const num = Number(value);
    if (isNaN(num)) return value;

    const locale = isIndianLocale() ? "en-IN" : navigator?.language || "en-US";
    const formatted = num.toLocaleString(locale, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    });
    return `${prefix}${formatted}${suffix}`;
  } catch (error) {
    return value;
  }
};

export const getChartTypeValue = (chartTypeConstant) => {
  try {
    if (!chartTypeConstant) return null;
    return chartTypeConstant.replace("_chart", "");
  } catch (error) {
    console.error("An error occurred while getting chart type value: ", error);
    return null;
  }
};

export const normalizeChartType = (chartType) => {
  try {
    if (!chartType) return null;
    if (chartType.endsWith("_chart")) return chartType;
    return `${chartType}_chart`;
  } catch (error) {
    console.error("An error occurred while normalizing chart type: ", error);
    return chartType;
  }
};

const validateFilterConditions = (conditions) => {
  try {
    if (!Array.isArray(conditions)) {
      return { isValid: true, message: "" };
    }

    for (const condition of conditions) {
      if (!condition?.property) {
        return {
          isValid: false,
          message: filterValidationMessages[filterFieldConfig.PROPERTY],
        };
      }

      if (!condition?.operator) {
        return {
          isValid: false,
          message: filterValidationMessages[filterFieldConfig.OPERATOR],
        };
      }

      if (requiresValue(condition.operator)) {
        if (Array.isArray(condition.value)) {
          if (!hasValidArrayValue(condition.value)) {
            return {
              isValid: false,
              message: filterValidationMessages[filterFieldConfig.VALUE],
            };
          }
        } else if (!isPresent(condition.value)) {
          return {
            isValid: false,
            message: filterValidationMessages[filterFieldConfig.VALUE],
          };
        }
      }
    }

    return { isValid: true, message: "" };
  } catch (error) {
    console.error("Error validating filter conditions: ", error);
    return { isValid: false, message: "Invalid filter configuration" };
  }
};

const AGG_PATTERN = new RegExp(`\\b(${aggregationOperators.join("|")})\\b`, "i");

export const validateFormulaStructure = (formula) => {
  try {
    if (!formula?.trim()) return false;

    const f = formula.trim();

    const isConditional = /\bIF\b/i.test(f);

    if (!isConditional && !/[+\-*/]/.test(f) && !AGG_PATTERN.test(f)) return false;

    if (isConditional && !/\bTHEN\b/i.test(f)) return false;

    if (/^[+\-*/]|[+\-*/]\s*$/.test(f)) return false;

    if (/[+\-*/]\s*[+\-*/]/.test(f)) return false;

    if (new RegExp(`\\b(${aggregationOperators.join("|")})\\b(?!\\s*\\()`, "i").test(f)) return false;

    if (new RegExp(`\\b(${aggregationOperators.join("|")})\\s*\\(\\s*\\)`, "i").test(f)) return false;

    if (/[}\)\d]\s*[{(]|[}\)]\s*\d/.test(f)) return false;

    let depth = 0;
    for (const ch of f) {
      if (ch === "(") depth++;
      if (ch === ")") depth--;
      if (depth < 0) return false;
    }
    if (depth !== 0) return false;

    return true;
  } catch (error) {
    console.error("Error validating formula structure: ", error);
    return false;
  }
};

export const validateChartConfiguration = (
  chartType,
  chartConfig,
  selectedObjects,
  filters = null,
  calculatedFields = null,
) => {
  try {
    if (!selectedObjects?.primary) {
      return { isValid: false, message: "Select a primary object to continue" };
    }

    if (!chartType) {
      return { isValid: false, message: "Select a chart type to continue" };
    }

    const rules = chartValidationRules[chartType];
    if (!rules) {
      return { isValid: true, message: "" };
    }

    for (const field of rules.requiredFields) {
      const fieldData = chartConfig?.[field];

      // Special handling for funnel stages (needs minimum stages check)
      if (field === "stages" && rules.minStages) {
        const validStages =
          fieldData?.filter((stage) => stage?.id && stage?.name) || [];

        if (validStages.length < rules.minStages) {
          return { isValid: false, message: rules.messages[field] };
        }
        continue;
      }

      const hasValidData =
        fieldData &&
        Array.isArray(fieldData) &&
        fieldData.length > 0 &&
        fieldData.some((item) => item?.id);

      if (!hasValidData) {
        return { isValid: false, message: rules.messages[field] };
      }
    }
    const groups = filters?.groups || [];
    for (const group of groups) {
      const filterValidation = validateFilterConditions(group?.conditions);
      if (!filterValidation?.isValid) {
        return filterValidation;
      }
    }

    if (calculatedFields && calculatedFields.length > 0) {
      for (const field of calculatedFields) {
        if (!field.formula?.trim() || !validateFormulaStructure(field.formula)) {
          return {
            isValid: false,
            message: `Complete the formula for "${field.name || "calculated field"}"`,
          };
        }
      }
    }

    return { isValid: true, message: "" };
  } catch (error) {
    console.error("Error validating chart configuration: ", error);
    return { isValid: false, message: "Invalid configuration" };
  }
};

const convertLegacyFiltersToGroups = (conditions) => {
  if (!conditions || conditions.length === 0) {
    return { groups: [] };
  }

  const groups = [];
  let currentGroup = {
    id: `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    conditions: [],
    logicWithNext: "AND",
  };

  conditions.forEach((condition, index) => {
    currentGroup.conditions.push({ ...condition });

    if (condition.logicWithNext === "AND" || index === conditions.length - 1) {
      if (index < conditions.length - 1) {
        currentGroup.logicWithNext = "AND";
      }
      groups.push(currentGroup);
      currentGroup = {
        id: `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        conditions: [],
        logicWithNext: "AND",
      };
    }
  });

  return { groups };
};

export const parseCompositeId = (id) => {
  if (!id || typeof id !== "string") return null;
  const match = id.match(/^([a-z_]+)_(\d+)$/);
  if (match) {
    return { slug: match[1], formId: parseInt(match[2], 10) };
  }
  return null;
};

const similarChartGroups = [
  [
    analyticsCharts.VERTICAL,
    analyticsCharts.HORIZONTAL,
    analyticsCharts.LINE,
    analyticsCharts.SCATTER,
    analyticsCharts.AREA,
  ],
  [analyticsCharts.PIE, analyticsCharts.DONUT],
  [analyticsCharts.TABLE, analyticsCharts.PIVOT_TABLE],
];

export const areChartsInSameGroup = (chartTypeA, chartTypeB) => {
  if (!chartTypeA || !chartTypeB) return false;
  return similarChartGroups.some(
    (group) => group.includes(chartTypeA) && group.includes(chartTypeB),
  );
};

export const getCommonFields = (oldChartType, newChartType) => {
  const oldFields = chartConfigurations[oldChartType] || [];
  const newFields = chartConfigurations[newChartType] || [];
  const oldFieldKeys = new Set(oldFields.map((f) => f.field));
  return newFields.map((f) => f.field).filter((key) => oldFieldKeys.has(key));
};

const configFieldKeys = [
  "x_axis", "y_axis", "break_down_by", "compare_by", "group_by",
  "rows", "columns", "values", "view_point_detail_by", "view_point_size_by",
  "sort_by", "gauge_value", "gauge_compare_by",
];

const resolveFieldInstance = (instance, availableProperties, idField = "id") => {
  if (!instance) return instance;

  const instanceId = typeof instance === "object" ? instance[idField] : instance;
  if (!instanceId) return instance;

  const directMatch = availableProperties.find((p) => p.id === instanceId || p.slug === instanceId);
  if (directMatch) {
    if (typeof instance === "object") {
      return { ...instance, [idField]: directMatch.id };
    }
    return directMatch.id;
  }

  return instance;
};

export const resolveChartConfigurationFields = (chartConfig, availableProperties) => {
  if (!chartConfig || !availableProperties?.length) return chartConfig;

  const resolved = { ...chartConfig };

  configFieldKeys.forEach((key) => {
    const fieldValue = resolved[key];
    if (!Array.isArray(fieldValue)) return;

    resolved[key] = fieldValue.map((instance) =>
      resolveFieldInstance(instance, availableProperties),
    );
  });

  if (Array.isArray(resolved.stages)) {
    resolved.stages = resolved.stages.map((stage) =>
      resolveFieldInstance(stage, availableProperties, "field_id"),
    );
  }

  return resolved;
};

export const applyDashboardConfiguration = async (config, store) => {
  try {
    if (config.primary_object_id || config.secondary_object_ids) {
      const crmObjects = store.state.analyticsConfiguration?.crmObjects || [];
      const storeObjects = store.getters["objects/objects"] || [];
      const hardcodedObjects = analyticsHardcodedSchemaObjects?.complex || [];
      const allObjects = [...(crmObjects.length > 0 ? crmObjects : storeObjects), ...hardcodedObjects];

      const primaryObjectID = config.primary_object_id;
      const secondaryObjectIDs = config.secondary_object_ids
        ? typeof config.secondary_object_ids === "string"
          ? JSON.parse(config.secondary_object_ids)
          : config.secondary_object_ids
        : [];

      const matchObject = (obj, targetId) => {
        const target = String(targetId);
        return (
          String(obj.id) === target ||
          String(obj.slug) === target ||
          (obj.object?.id && String(obj.object.id) === target) ||
          (obj.object?.uuid && String(obj.object.uuid) === target)
        );
      };

      const compositeId = parseCompositeId(primaryObjectID);

      if (compositeId && compositeId.slug === "forms") {
        const formsObject = allObjects.find((obj) => obj.slug === "forms");
        if (formsObject) {
          const FormService = (await import("@/src/api/FormService.js")).default;
          const response = await FormService.schema.fetchFormSchema(
            compositeId.formId,
          );
          if (response?.success && response?.data) {
            await store.dispatch("analyticsConfiguration/setSelectedForm", {
              formId: compositeId.formId,
              schema: response,
            });
            const formObject = {
              id: `forms_${compositeId.formId}`,
              slug: "forms",
              name: response.data.name || "Form",
              formId: compositeId.formId,
            };
            const secondaryObjects = allObjects.filter((obj) =>
              secondaryObjectIDs.some((secId) => matchObject(obj, secId)),
            );
            await store.dispatch(
              "analyticsConfiguration/updateSelectedObjects",
              {
                primary: formObject,
                secondary: secondaryObjects || [],
                skipReset: true,
              },
            );
          }
        }
      } else {
        const primaryObject = allObjects.find(
          (obj) => matchObject(obj, primaryObjectID),
        );
        const secondaryObjects = allObjects.filter((obj) =>
          secondaryObjectIDs.some((secId) => matchObject(obj, secId)),
        );
        await store.dispatch("analyticsConfiguration/updateSelectedObjects", {
          primary: primaryObject || null,
          secondary: secondaryObjects || [],
          skipReset: true,
        });
      }
    }

    const batchPayload = {};

    if (config.chart_type) {
      const chartTypeWithSuffix = `${config.chart_type}_chart`;
      batchPayload.selectedChartType = chartTypeWithSuffix;
      batchPayload.renderedChartType = chartTypeWithSuffix;
    }

    if (config.chart_configuration) {
      const rawChartConfig =
        typeof config.chart_configuration === "string"
          ? JSON.parse(config.chart_configuration)
          : config.chart_configuration;
      const rehydrated = rehydrateConfigFromBackend(rawChartConfig);
      const availableProperties = store.getters["analyticsConfiguration/availableProperties"] || [];
      const chartConfig = resolveChartConfigurationFields(rehydrated, availableProperties);
      batchPayload.chartConfiguration = chartConfig;
      batchPayload.renderedChartConfiguration = chartConfig;
    }

    if (config.filters) {
      let filters = { groups: [] };
      const parsed =
        typeof config.filters === "string"
          ? JSON.parse(config.filters)
          : config.filters;

      if (parsed.conditions && !parsed.groups) {
        filters = convertLegacyFiltersToGroups(parsed.conditions);
      } else if (parsed.groups) {
        filters = parsed;
      }

      filters = rehydrateFiltersFromBackend(filters);

      const availableProps = store.getters["analyticsConfiguration/availableProperties"] || [];
      if (availableProps.length > 0 && filters.groups) {
        filters = {
          ...filters,
          groups: filters.groups.map((group) => ({
            ...group,
            conditions: (group.conditions || []).map((condition) => {
              if (!condition.property) return condition;
              const match = availableProps.find(
                (p) => p.id === condition.property || p.slug === condition.property,
              );
              if (match && match.id !== condition.property) {
                return { ...condition, property: match.id };
              }
              return condition;
            }),
          })),
        };
      }

      batchPayload.filters = filters;
    }

    if (config.calculated_fields) {
      batchPayload.calculatedFields =
        typeof config.calculated_fields === "string"
          ? JSON.parse(config.calculated_fields)
          : config.calculated_fields;
    }

    if (config.chart_data) {
      batchPayload.chartData =
        typeof config.chart_data === "string"
          ? JSON.parse(config.chart_data)
          : config.chart_data;
    }

    batchPayload.configurationExists = true;

    await store.dispatch(
      "analyticsConfiguration/applyConfigurationBatch",
      batchPayload,
    );

    if (config.sql_query) {
      await store.dispatch(
        "analyticsConfiguration/setSqlQuery",
        config.sql_query,
      );
    }

    if (config.total !== undefined) {
      await store.dispatch(
        "analyticsConfiguration/setConfigurationTotal",
        config.total,
      );
    }
  } catch (error) {
    console.error("Error applying dashboard configuration: ", error);
    throw error;
  }
};

export const buildConfigurationPayload = (store, dashboardType, status = analyticsDashboardStatuses.DRAFT) => {
  try {
    const state = store.state.analyticsConfiguration;
    const filters = state.filters?.groups || [];
    const calculatedFields = state.calculatedFields || [];

    let configPayload = {
      type: dashboardType,
      chart_type: getChartTypeValue(state.selectedChartType),
      primary_object_id: state.selectedObjects.primary?.id || null,
      secondary_object_ids: state.selectedObjects.secondary.map((obj) => obj.id) || [],
      chart_configuration: JSON.stringify(decomposeConfigForBackend(state.chartConfiguration)),
      filters: JSON.stringify(decomposeFiltersForBackend({ groups: filters })),
      calculated_fields: JSON.stringify(calculatedFields),
      status: status,
    };

    if (dashboardType === "sql") {
      configPayload.sql_query = state.sqlQuery;
    }

    return configPayload;
  } catch (error) {
    console.error("Error building configuration payload: ", error);
    return null;
  }
};

// --- Composite key helpers for multi-object attribute disambiguation ---

const COMPOSITE_KEY_SEPARATOR = "::";

export function makeCompositeKey(objectSlug, attrId) {
  return `${objectSlug}${COMPOSITE_KEY_SEPARATOR}${attrId}`;
}

export function parseCompositePropertyKey(key) {
  if (!key || typeof key !== "string") return { id: key };
  const idx = key.indexOf(COMPOSITE_KEY_SEPARATOR);
  if (idx === -1) return { id: key };
  return {
    object_slug: key.substring(0, idx),
    id: key.substring(idx + COMPOSITE_KEY_SEPARATOR.length),
  };
}

export function isCompositePropertyKey(key) {
  return typeof key === "string" && key.includes(COMPOSITE_KEY_SEPARATOR);
}

function decomposeConfigItem(item) {
  if (!item || typeof item !== "object") return item;
  const result = { ...item };

  if (result.id && isCompositePropertyKey(result.id)) {
    const parsed = parseCompositePropertyKey(result.id);
    result.id = parsed.id;
    result.object_slug = parsed.object_slug;
  }

  if (result.field_id && isCompositePropertyKey(result.field_id)) {
    const parsed = parseCompositePropertyKey(result.field_id);
    result.field_id = parsed.id;
    result.field_object_slug = parsed.object_slug;
  }

  if (result.filters?.conditions) {
    result.filters = {
      ...result.filters,
      conditions: result.filters.conditions.map((condition) => {
        if (condition.property && isCompositePropertyKey(condition.property)) {
          const parsed = parseCompositePropertyKey(condition.property);
          return { ...condition, property: parsed.id, object_slug: parsed.object_slug };
        }
        return condition;
      }),
    };
  }

  return result;
}

export function decomposeConfigForBackend(config) {
  if (!config) return config;
  const result = {};
  for (const key of Object.keys(config)) {
    const value = config[key];
    if (Array.isArray(value)) {
      result[key] = value.map((item) => decomposeConfigItem(item));
    } else {
      result[key] = value;
    }
  }
  return result;
}

export function decomposeFiltersForBackend(filters) {
  if (!filters?.groups) return filters;
  return {
    ...filters,
    groups: filters.groups.map((group) => ({
      ...group,
      conditions: (group.conditions || []).map((condition) => {
        if (condition.property && isCompositePropertyKey(condition.property)) {
          const parsed = parseCompositePropertyKey(condition.property);
          return { ...condition, property: parsed.id, object_slug: parsed.object_slug };
        }
        return condition;
      }),
    })),
  };
}

function rehydrateConfigItem(item) {
  if (!item || typeof item !== "object") return item;
  const result = { ...item };

  if (result.object_slug && result.id) {
    result.id = makeCompositeKey(result.object_slug, result.id);
    delete result.object_slug;
  }

  if (result.field_object_slug && result.field_id) {
    result.field_id = makeCompositeKey(result.field_object_slug, result.field_id);
    delete result.field_object_slug;
  }

  if (result.filters?.conditions) {
    result.filters = {
      ...result.filters,
      conditions: result.filters.conditions.map((condition) => {
        if (condition.object_slug && condition.property) {
          const compositeKey = makeCompositeKey(condition.object_slug, condition.property);
          const { object_slug, ...rest } = condition;
          return { ...rest, property: compositeKey };
        }
        return condition;
      }),
    };
  }

  return result;
}

export function rehydrateConfigFromBackend(config) {
  if (!config) return config;
  const result = {};
  for (const key of Object.keys(config)) {
    const value = config[key];
    if (Array.isArray(value)) {
      result[key] = value.map((item) => rehydrateConfigItem(item));
    } else {
      result[key] = value;
    }
  }
  return result;
}

export function rehydrateFiltersFromBackend(filters) {
  if (!filters?.groups) return filters;
  return {
    ...filters,
    groups: filters.groups.map((group) => ({
      ...group,
      conditions: (group.conditions || []).map((condition) => {
        if (condition.object_slug && condition.property) {
          const compositeKey = makeCompositeKey(condition.object_slug, condition.property);
          const { object_slug, ...rest } = condition;
          return { ...rest, property: compositeKey };
        }
        return condition;
      }),
    })),
  };
}

// --- Migration helpers for object selection changes ---
// When multipleObjects toggles, property IDs switch format (bare ↔ composite).
// This migrates existing chartConfiguration, filters, and calculatedFields
// so their IDs match the new availableProperties.

export function buildPropertyIdMigrationMap(oldProperties, newProperties) {
  const map = {};
  for (const newProp of newProperties) {
    const oldProp = oldProperties.find(
      (p) => p.rawId === newProp.rawId && p.objectSlug === newProp.objectSlug,
    );
    if (oldProp && oldProp.id !== newProp.id) {
      map[oldProp.id] = newProp.id;
    }
  }
  return map;
}

function migrateConfigItem(item, migrationMap) {
  if (!item || typeof item !== "object") return item;
  const result = { ...item };

  if (result.id && migrationMap[result.id]) {
    result.id = migrationMap[result.id];
  }

  if (result.field_id && migrationMap[result.field_id]) {
    result.field_id = migrationMap[result.field_id];
  }

  if (result.filters?.conditions) {
    result.filters = {
      ...result.filters,
      conditions: result.filters.conditions.map((condition) => {
        if (condition.property && migrationMap[condition.property]) {
          return { ...condition, property: migrationMap[condition.property] };
        }
        return condition;
      }),
    };
  }

  return result;
}

export function migrateChartConfig(config, migrationMap) {
  if (!config) return config;
  const result = {};
  for (const key of Object.keys(config)) {
    const value = config[key];
    if (Array.isArray(value)) {
      result[key] = value.map((item) => migrateConfigItem(item, migrationMap));
    } else {
      result[key] = value;
    }
  }
  return result;
}

export function migrateFilters(filters, migrationMap) {
  if (!filters?.groups) return filters;
  return {
    ...filters,
    groups: filters.groups.map((group) => ({
      ...group,
      conditions: (group.conditions || []).map((condition) => {
        if (condition.property && migrationMap[condition.property]) {
          return { ...condition, property: migrationMap[condition.property] };
        }
        return condition;
      }),
    })),
  };
}

export function migrateCalculatedFieldFormulas(fields, migrationMap) {
  if (!fields || fields.length === 0) return fields;
  return fields.map((field) => {
    let formula = field.formula;
    if (!formula) return field;
    for (const [oldId, newId] of Object.entries(migrationMap)) {
      formula = formula.replaceAll(`{${oldId}}`, `{${newId}}`);
    }
    if (formula === field.formula) return field;
    return { ...field, formula };
  });
}
